import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

export function isHlsPlaybackUrl(url?: string | null): boolean {
  return Boolean(url && (url.includes('.m3u8') || url.includes('/hls/')));
}

export interface HlsVideoPlayerProps extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'src'> {
  src?: string | null;
  fallbackLinkClassName?: string;
  /** Fill portrait frame — contain keeps full vertical video visible */
  fit?: 'contain' | 'cover';
}

const HlsVideoPlayer: React.FC<HlsVideoPlayerProps> = ({
  src,
  fallbackLinkClassName = 'btn btn-sm btn-outline-primary',
  fit = 'contain',
  className,
  style,
  onClick,
  poster,
  ...videoProps
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) {
      return undefined;
    }

    setError(null);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    video.removeAttribute('src');
    video.load();

    if (!isHlsPlaybackUrl(src)) {
      video.src = src;
      return undefined;
    }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      return undefined;
    }

    if (!Hls.isSupported()) {
      setError('HLS playback is not supported in this browser.');
      return undefined;
    }

    const hls = new Hls({ enableWorker: true });
    hlsRef.current = hls;
    hls.loadSource(src);
    hls.attachMedia(video);
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;
      setError('Could not load video stream.');
      hls.destroy();
      hlsRef.current = null;
    });

    return () => {
      hls.destroy();
      hlsRef.current = null;
    };
  }, [src]);

  if (!src) {
    return null;
  }

  if (error) {
    return (
      <div className="text-center small">
        <p className="text-muted mb-2">{error}</p>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className={fallbackLinkClassName}
          onClick={onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        >
          Open stream
        </a>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      className={className}
      style={{ objectFit: fit, backgroundColor: '#0d1117', ...style }}
      onClick={onClick}
      poster={poster}
      {...videoProps}
    />
  );
};

export default HlsVideoPlayer;
